import {
  faAlignLeft,
  faArrowsLeftRight,
  faArrowsUpDown,
  faChevronDown,
  faImage,
  faObjectsAlignCenterHorizontal,
  faObjectsAlignLeft,
  faObjectsAlignRight,
  faPlus,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import {
  IMAGE_ASPECT_OPTIONS,
  type ImageBlockAspect,
} from '@/components/blocks/primitives/image-presentation';
import {
  ALIGNMENT_TOOLTIP_CONTENT_PROPS,
  OptionButtonGroup,
  type OptionButtonOption,
  SettingRow,
} from '@/components/page-editor/settings-panel/fields';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import type { AccordionIconStyle } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { RadioCardGroup } from '@/components/ui/form/radio-card-group';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Switch } from '@/components/ui/switch';
import { SmallText } from '@/components/ui/typography';
import type {
  CarouselOrientation,
  CarouselVerticalHeight,
  CollectionCardAlign,
  CollectionImagePosition,
} from '@/lib/blocks/collection/blocks';
import type { RequestedCollectionMediaSize } from '@/lib/blocks/collection/media-layout';
import { parseCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import type { LayoutRenderer } from '@/lib/blocks/collection/variant-registry';
import { IMAGE_SIZE_OPTIONS } from '@/lib/blocks/image-size';
import type { GroupChildFloat } from '@/lib/blocks/layout/group-layout';
import { readBoolean, readRecord, readString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';

function readCountProp(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 && n <= 6 ? n : fallback;
}

type SizeValue = 'sm' | 'md' | 'lg';
type PaddingValue = 'none' | SizeValue;

function readSizeValue(value: unknown, fallback: SizeValue): SizeValue {
  const s = readString(value);
  return s === 'sm' || s === 'md' || s === 'lg' ? s : fallback;
}

function readPaddingValue(value: unknown, fallback: PaddingValue): PaddingValue {
  const s = readString(value);
  return s === 'none' || s === 'sm' || s === 'md' || s === 'lg' ? s : fallback;
}

const SIZE_OPTIONS: OptionButtonOption<SizeValue>[] = [
  { value: 'sm', label: 'S' },
  { value: 'md', label: 'M' },
  { value: 'lg', label: 'L' },
];

const PADDING_OPTIONS: OptionButtonOption<PaddingValue>[] = [
  { value: 'none', label: 'None' },
  ...SIZE_OPTIONS,
];

const ALIGN_OPTIONS: {
  value: CollectionCardAlign;
  icon: typeof faObjectsAlignLeft;
  label: string;
}[] = [
  { value: 'start', icon: faObjectsAlignLeft, label: 'Left' },
  { value: 'center', icon: faObjectsAlignCenterHorizontal, label: 'Center' },
  { value: 'end', icon: faObjectsAlignRight, label: 'Right' },
];

const IMAGE_POSITION_OPTIONS: { value: CollectionImagePosition; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'top', label: 'Stacked' },
  { value: 'right', label: 'Right' },
  { value: 'background', label: 'Background' },
];

const ACCORDION_ICON_OPTIONS: { value: AccordionIconStyle; icon: typeof faChevronDown }[] = [
  { value: 'chevron', icon: faChevronDown },
  { value: 'plus', icon: faPlus },
];

function ImagePositionIcon({ position }: { position: CollectionImagePosition }) {
  const img = <Icon icon={faImage} className="size-3.5" />;
  const lines = <Icon icon={faAlignLeft} className="size-3.5" />;
  return (
    <div className="flex h-6 items-center justify-center">
      {position === 'background' ? (
        <div className="relative flex items-center justify-center">
          {img}
          <Icon icon={faAlignLeft} className="absolute size-2.5 opacity-60" />
        </div>
      ) : position === 'top' ? (
        <div className="flex flex-col items-center gap-0.5">
          {img}
          {lines}
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className={cn(position === 'right' && 'order-2')}>{img}</span>
          <span className={cn(position === 'right' && 'order-1')}>{lines}</span>
        </div>
      )}
    </div>
  );
}

export function SettingsTab({
  variantProps,
  renderer,
  cardAlign,
  onVariantPropsChange,
  onCardAlignChange,
  showBackgroundPosition = false,
  showLayoutControls = true,
  showImagePosition: showImagePositionControl = true,
  showColumns: showColumnsControl = true,
  float = null,
  onFloatChange,
  imageAspect,
  onImageAspectChange,
}: {
  variantProps: Record<string, unknown>;
  renderer: LayoutRenderer;
  cardAlign: CollectionCardAlign;
  onVariantPropsChange: (patch: Record<string, unknown>) => void;
  onCardAlignChange: (align: CollectionCardAlign) => void;
  showBackgroundPosition?: boolean;
  showLayoutControls?: boolean;
  showImagePosition?: boolean;
  showColumns?: boolean;
  float?: GroupChildFloat | null;
  onFloatChange?: (float: GroupChildFloat | null) => void;
  imageAspect?: ImageBlockAspect;
  onImageAspectChange?: (aspect: ImageBlockAspect) => void;
}) {
  const columnsRecord = readRecord(variantProps.columns) ?? {};
  const ipvRecord = readRecord(variantProps.itemsPerView) ?? {};
  const gapValue = readSizeValue(variantProps.gap, 'md');
  const paddingValue = readPaddingValue(variantProps.padding, 'md');
  const mediaLayout = parseCollectionMediaLayout(variantProps);
  const imagePosition = mediaLayout.position;
  const orientationValue = (readString(variantProps.orientation) ??
    'horizontal') as CarouselOrientation;
  const arrowsEnabled = readBoolean(variantProps.arrows) ?? true;
  const autoScrollValue = readBoolean(variantProps.autoScroll) ?? false;
  const verticalHeightValue = (readString(variantProps.verticalHeight) ??
    'md') as CarouselVerticalHeight;

  // Accordion-specific
  const expandFirstItem = readBoolean(variantProps.expandFirstItem) ?? false;
  const allowMultiple = readBoolean(variantProps.allowMultiple) ?? true;
  const showDividers = readBoolean(variantProps.showDividers) ?? true;
  const iconStyle: AccordionIconStyle =
    readString(variantProps.iconStyle) === 'plus' ? 'plus' : 'chevron';

  const desktopCols = readCountProp(columnsRecord.desktop, 3);
  const desktopIpv = readCountProp(ipvRecord.desktop, 3);

  const isCarousel = renderer === 'carousel';
  const isAccordion = renderer === 'accordion';
  const isVertical = isCarousel && orientationValue === 'vertical';
  const isHorizontalCarousel = isCarousel && !isVertical;
  const showColumns = showColumnsControl && renderer !== 'alternating' && !isVertical;
  const showImagePosition =
    showImagePositionControl && (renderer === 'grid' || renderer === 'carousel');
  const showMediaSize = showImagePositionControl && renderer !== 'accordion';
  const canShowBackground =
    renderer === 'carousel' || (showBackgroundPosition && renderer === 'grid');
  const positionOptions = canShowBackground
    ? IMAGE_POSITION_OPTIONS
    : IMAGE_POSITION_OPTIONS.filter((opt) => opt.value !== 'background');
  const columnMax = isCarousel ? 6 : isAccordion ? 3 : 4;

  return (
    <div className="space-y-5">
      {showColumns && showLayoutControls && (
        <SettingRow
          title={isCarousel ? 'Items per View' : 'Columns'}
          description={
            isCarousel
              ? 'Number of items per view (automatically adjusts on smaller screens)'
              : 'Number of columns per row (automatically adjusts on smaller screens)'
          }
        >
          <ButtonGroup>
            {Array.from({ length: columnMax }, (_, i) => i + 1).map((n) => {
              const active = isCarousel ? desktopIpv === n : desktopCols === n;
              return (
                <Button
                  key={n}
                  variant={active ? 'solid' : 'outline'}
                  colorScheme="secondary"
                  className="h-8 min-w-[2rem] px-2.5 text-xs font-semibold"
                  onClick={() =>
                    isCarousel
                      ? onVariantPropsChange({
                          itemsPerView: {
                            ...ipvRecord,
                            desktop: n,
                            tablet: Math.min(n, 2),
                            mobile: 1,
                          },
                        })
                      : onVariantPropsChange({
                          columns: {
                            ...columnsRecord,
                            desktop: n,
                            tablet: Math.min(n, 2) as 1 | 2,
                            mobile: 1,
                          },
                        })
                  }
                >
                  {n}
                </Button>
              );
            })}
          </ButtonGroup>
        </SettingRow>
      )}

      {isCarousel && showLayoutControls && (
        <SettingRow title="Orientation" description="Direction of the carousel scroll">
          <ButtonGroup className="w-full">
            {[
              {
                value: 'horizontal' as CarouselOrientation,
                icon: faArrowsLeftRight,
                label: 'Horizontal',
              },
              { value: 'vertical' as CarouselOrientation, icon: faArrowsUpDown, label: 'Vertical' },
            ].map((opt) => (
              <Button
                key={opt.value}
                variant={orientationValue === opt.value ? 'solid' : 'outline'}
                colorScheme="secondary"
                className="h-auto flex-1 flex-col gap-1 py-1.5 text-xs"
                onClick={() => onVariantPropsChange({ orientation: opt.value })}
              >
                <Icon icon={opt.icon} className="size-4" />
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </SettingRow>
      )}

      {isVertical && showLayoutControls && (
        <SettingRow title="Height" description="Height of the vertical carousel container">
          <OptionButtonGroup
            options={SIZE_OPTIONS}
            value={verticalHeightValue}
            onChange={(v) => onVariantPropsChange({ verticalHeight: v })}
            buttonClassName="px-3 text-xs font-semibold"
          />
        </SettingRow>
      )}

      {isHorizontalCarousel && showLayoutControls && (
        <SettingRow
          title="Infinite Scroll"
          description="Continuously scroll through items automatically"
        >
          <Switch
            checked={autoScrollValue}
            onCheckedChange={(checked) => onVariantPropsChange({ autoScroll: checked })}
          />
        </SettingRow>
      )}

      {isCarousel && !autoScrollValue && !isVertical && showLayoutControls && (
        <SettingRow title="Navigation Arrows" description="Show navigation arrows on the carousel.">
          <Switch
            checked={arrowsEnabled}
            onCheckedChange={(checked) => onVariantPropsChange({ arrows: checked })}
          />
        </SettingRow>
      )}

      <SettingRow title="Gap" description="Spacing between cards">
        <OptionButtonGroup
          options={SIZE_OPTIONS}
          value={gapValue}
          onChange={(v) => onVariantPropsChange({ gap: v })}
          buttonClassName="px-3 text-xs font-semibold"
        />
      </SettingRow>

      <SettingRow title="Padding" description="Spacing inside cards">
        <OptionButtonGroup
          options={PADDING_OPTIONS}
          value={paddingValue}
          onChange={(v) => onVariantPropsChange({ padding: v })}
          buttonClassName="px-3 text-xs font-semibold"
        />
      </SettingRow>

      {isAccordion && showLayoutControls && (
        <>
          <SettingRow title="Expand First Item" description="Automatically expand the first item">
            <Switch
              checked={expandFirstItem}
              onCheckedChange={(checked) => onVariantPropsChange({ expandFirstItem: checked })}
            />
          </SettingRow>

          <SettingRow
            title="Allow Multiple Open Items"
            description="Allow multiple items to be expanded at once"
          >
            <Switch
              checked={allowMultiple}
              onCheckedChange={(checked) => onVariantPropsChange({ allowMultiple: checked })}
            />
          </SettingRow>

          {desktopCols === 1 && (
            <SettingRow title="Dividers" description="Display a border between items">
              <Switch
                checked={showDividers}
                onCheckedChange={(checked) => onVariantPropsChange({ showDividers: checked })}
              />
            </SettingRow>
          )}

          <SettingRow
            title="Icon"
            description="Choose the icon used to expand and collapse accordion items"
          >
            <ButtonGroup>
              {ACCORDION_ICON_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={iconStyle === opt.value ? 'solid' : 'outline'}
                  colorScheme="secondary"
                  className="h-8 w-10 px-0"
                  onClick={() => onVariantPropsChange({ iconStyle: opt.value })}
                >
                  <Icon icon={opt.icon} className="size-4" />
                </Button>
              ))}
            </ButtonGroup>
          </SettingRow>
        </>
      )}

      {(!isAccordion || !showLayoutControls) && (
        <SettingRow title="Content Alignment" description="How content is aligned within each card">
          <ButtonGroup>
            {ALIGN_OPTIONS.map((opt) => (
              <IconTooltipButton
                key={opt.value}
                tooltip={opt.label}
                tooltipContentProps={ALIGNMENT_TOOLTIP_CONTENT_PROPS}
                variant={cardAlign === opt.value ? 'solid' : 'outline'}
                colorScheme="secondary"
                size="icon"
                className="h-8 w-10 px-0"
                onClick={() => onCardAlignChange(opt.value)}
              >
                <Icon icon={opt.icon} className="size-4" />
              </IconTooltipButton>
            ))}
          </ButtonGroup>
        </SettingRow>
      )}

      {showMediaSize && imagePosition !== 'background' && onImageAspectChange && (
        <SettingRow title="Image Aspect" description="Shape of the image frame on every card">
          <OptionButtonGroup<ImageBlockAspect>
            options={IMAGE_ASPECT_OPTIONS}
            value={imageAspect}
            onChange={onImageAspectChange}
            buttonClassName="px-2 text-xs font-semibold"
          />
        </SettingRow>
      )}

      {showMediaSize && imagePosition !== 'background' && (
        <SettingRow
          title="Image Size"
          description="Controls image width while preserving its aspect ratio"
        >
          <div className="space-y-1.5">
            <OptionButtonGroup<RequestedCollectionMediaSize>
              options={IMAGE_SIZE_OPTIONS}
              value={mediaLayout.size}
              onChange={(imageSize) => onVariantPropsChange({ imageSize })}
              buttonClassName="min-w-9 px-2 text-xs font-semibold"
            />
            {mediaLayout.size === 'auto' && (
              <SmallText className="text-right text-xs text-muted-foreground">
                Inferred per image
              </SmallText>
            )}
          </div>
        </SettingRow>
      )}

      {showImagePosition && showLayoutControls && (
        <div className="space-y-2">
          <SmallText className="font-semibold">Image Position</SmallText>
          <RadioCardGroup
            aria-label="Image position"
            className="w-full min-w-0"
            value={imagePosition}
            onValueChange={(imagePosition) => onVariantPropsChange({ imagePosition })}
            options={positionOptions.map((opt) => ({
              value: opt.value,
              title: opt.label,
              icon: <ImagePositionIcon position={opt.value} />,
            }))}
            gridColumns={canShowBackground ? 'grid-cols-4' : 'grid-cols-3'}
            gridGap="gap-1.5"
            hideRadio
            cardClassName="gap-1.5 p-2"
          />
          {(imagePosition === 'left' || imagePosition === 'right') && (
            <SmallText className="text-muted-foreground">
              Left and right stack when cards are too narrow.
            </SmallText>
          )}
        </div>
      )}

      {onFloatChange && (
        <FloatWrapControl
          value={float}
          onChange={onFloatChange}
          description="Float this collection so surrounding content in the same group wraps around it"
        />
      )}
    </div>
  );
}
